import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { Sync, Download, TrendingUp } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { evitalrxSalesAPI, lookupAPI } from '../services/api';
import { Store } from '../types';

interface DailySummaryRow {
  store_name: string | null;
  date: string;
  bill_count: number;
  total: string;
  cash: string;
  upi: string;
  card: string;
  unpaid: string;
}

interface SalesBill {
  id: number;
  store_id: number;
  bill_no: string;
  bill_date: string | null;
  entry_date: string | null;
  entry_by: string | null;
  patient_name: string | null;
  mobile: string | null;
  bill_amount: string;
  payment_method: string | null;
}

interface Props {
  selectedStore: Store | null;
}

const inr = (n: string | number | null) =>
  n == null || n === ''
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function fmtDate(val: string | null) {
  if (!val) return '—';
  return dayjs(val).format('DD-MMM-YY');
}

function paymentChip(method: string | null) {
  if (!method || method.trim() === '') return <Chip size="small" label="Unpaid" color="error" variant="outlined" />;
  const m = method.toLowerCase();
  if (m.includes('upi')) return <Chip size="small" label="UPI" color="primary" />;
  if (m.includes('cash')) return <Chip size="small" label="Cash" color="success" />;
  if (m.includes('cc') || m.includes('dc') || m.includes('card')) return <Chip size="small" label="Card" color="secondary" />;
  return <Chip size="small" label={method} />;
}

const EvitalrxSales: React.FC<Props> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    lookupAPI.getStores().then(setStores).catch(() => {});
  }, []);

  const storeOptions = [
    { value: 'all' as const, label: 'All Stores' },
    ...stores.map(s => ({ value: s.id, label: s.store_name })),
  ];

  // Sync controls
  const [syncStore, setSyncStore] = useState<number | 'all'>(selectedStore ? selectedStore.id : 'all');
  const [syncFrom, setSyncFrom] = useState<Dayjs | null>(dayjs());
  const [syncTo, setSyncTo]     = useState<Dayjs | null>(dayjs());
  const [syncing, setSyncing]   = useState(false);
  const [syncResult, setSyncResult] = useState<{ synced: number; skipped: number } | null>(null);
  const [syncError, setSyncError]   = useState('');

  // Daily summary tab
  const [summaryStore, setSummaryStore] = useState<number | 'all'>(selectedStore ? selectedStore.id : 'all');
  const [summaryFrom, setSummaryFrom]   = useState<Dayjs | null>(dayjs());
  const [summaryTo, setSummaryTo]       = useState<Dayjs | null>(dayjs());
  const [summary, setSummary]           = useState<DailySummaryRow[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // All bills tab
  const [billsStore, setBillsStore]       = useState<number | 'all'>(selectedStore ? selectedStore.id : 'all');
  const [billsFrom, setBillsFrom]         = useState<Dayjs | null>(dayjs());
  const [billsTo, setBillsTo]             = useState<Dayjs | null>(dayjs());
  const [entryByFilter, setEntryByFilter] = useState('');
  const [pmFilter, setPmFilter]           = useState('');
  const [bills, setBills]                 = useState<SalesBill[]>([]);
  const [billsLoading, setBillsLoading]   = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await evitalrxSalesAPI.getDailySummary({
        store_id: summaryStore,
        from_date: summaryFrom?.format('YYYY-MM-DD'),
        to_date: summaryTo?.format('YYYY-MM-DD'),
      });
      setSummary(data);
    } catch {
      setSummary([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [summaryStore, summaryFrom, summaryTo]);

  const loadBills = useCallback(async () => {
    setBillsLoading(true);
    try {
      const data = await evitalrxSalesAPI.getSales({
        store_id: billsStore,
        from_date: billsFrom?.format('YYYY-MM-DD'),
        to_date: billsTo?.format('YYYY-MM-DD'),
        entry_by: entryByFilter || undefined,
        payment_method: pmFilter || undefined,
      });
      setBills(data);
    } catch {
      setBills([]);
    } finally {
      setBillsLoading(false);
    }
  }, [billsStore, billsFrom, billsTo, entryByFilter, pmFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (tab === 1) loadBills(); }, [tab, loadBills]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setSyncError('');
    try {
      const res = await evitalrxSalesAPI.sync(
        syncStore,
        syncFrom?.format('YYYY-MM-DD'),
        syncTo?.format('YYYY-MM-DD'),
      );
      setSyncResult({ synced: res.synced, skipped: res.skipped });
      loadSummary();
      if (tab === 1) loadBills();
    } catch (e: any) {
      setSyncError(e?.response?.data?.message || 'Sync failed. Check scraper logs.');
    } finally {
      setSyncing(false);
    }
  };

  const exportBillsCSV = () => {
    const headers = ['Bill No', 'Bill Date', 'Entry By', 'Patient', 'Mobile', 'Amount', 'Payment'];
    const rows = bills.map(b => [
      b.bill_no,
      fmtDate(b.bill_date),
      b.entry_by || '',
      b.patient_name || '',
      b.mobile || '',
      b.bill_amount,
      b.payment_method || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportSummaryCSV = () => {
    const headers = ['Date', 'Bills', 'Cash', 'UPI', 'Card', 'Unpaid', 'Total'];
    const rows = summary.map(r => [r.date, r.bill_count, r.cash, r.upi, r.card, r.unpaid, r.total]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales_daily_${dayjs().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const grandTotal   = summary.reduce((s, r) => s + Number(r.total), 0);
  const grandCash    = summary.reduce((s, r) => s + Number(r.cash), 0);
  const grandUpi     = summary.reduce((s, r) => s + Number(r.upi), 0);
  const grandCard    = summary.reduce((s, r) => s + Number(r.card), 0);
  const grandUnpaid  = summary.reduce((s, r) => s + Number(r.unpaid), 0);
  const grandBills   = summary.reduce((s, r) => s + r.bill_count, 0);

  return (
    <Box sx={{ p: 2 }}>
      {/* Sync bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Sync from evitalrx:</Typography>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Store</InputLabel>
          <Select value={syncStore} label="Store" onChange={e => setSyncStore(e.target.value as number | 'all')}>
            {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>

        <DatePicker label="From" value={syncFrom} onChange={setSyncFrom}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
        <DatePicker label="To" value={syncTo} onChange={setSyncTo}
          slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />

        <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <Sync />}
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? 'Syncing…' : 'Sync Now'}
        </Button>

        {syncResult && (
          <Alert severity="success" sx={{ py: 0 }}>
            Synced {syncResult.synced} new bills, {syncResult.skipped} already exist
          </Alert>
        )}
        {syncError && <Alert severity="error" sx={{ py: 0 }}>{syncError}</Alert>}
        <Typography variant="caption" color="text.secondary">
          All stores are on the FirstShield POS — bills after each store’s switch date come live from the POS, no sync needed.
        </Typography>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Daily Summary" />
        <Tab label="All Bills" />
      </Tabs>

      {/* ── Tab 0: Daily Summary ── */}
      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Store</InputLabel>
              <Select value={summaryStore} label="Store" onChange={e => setSummaryStore(e.target.value as number | 'all')}>
                {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <DatePicker label="From" value={summaryFrom} onChange={setSummaryFrom}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
            <DatePicker label="To" value={summaryTo} onChange={setSummaryTo}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
            <Button size="small" onClick={loadSummary} disabled={summaryLoading}>Refresh</Button>
            <Button size="small" startIcon={<Download />} onClick={exportSummaryCSV} disabled={summary.length === 0}>
              CSV
            </Button>
          </Box>

          {summaryLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : summary.length === 0 ? (
            <Alert severity="info">No sales data found. Run a sync first.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    {summaryStore === 'all' && <TableCell><strong>Store</strong></TableCell>}
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="center"><strong>Bills</strong></TableCell>
                    <TableCell align="right"><strong>Cash</strong></TableCell>
                    <TableCell align="right"><strong>UPI</strong></TableCell>
                    <TableCell align="right"><strong>Card</strong></TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}><strong>Unpaid</strong></TableCell>
                    <TableCell align="right"><strong>Total</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.map((r) => (
                    <TableRow key={`${r.store_name}-${r.date}`} hover>
                      {summaryStore === 'all' && (
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {r.store_name || '—'}
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{r.date}</TableCell>
                      <TableCell align="center">{r.bill_count}</TableCell>
                      <TableCell align="right" sx={{ color: '#2e7d32' }}>{inr(r.cash)}</TableCell>
                      <TableCell align="right" sx={{ color: '#1565c0' }}>{inr(r.upi)}</TableCell>
                      <TableCell align="right" sx={{ color: '#6a1b9a' }}>{inr(r.card)}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main' }}>{inr(r.unpaid)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(r.total)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Totals row */}
                  <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                    {summaryStore === 'all' && <TableCell />}
                    <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>{grandBills}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#2e7d32' }}>{inr(grandCash)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#1565c0' }}>{inr(grandUpi)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#6a1b9a' }}>{inr(grandCard)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main' }}>{inr(grandUnpaid)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{inr(grandTotal)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ── Tab 1: All Bills ── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Store</InputLabel>
              <Select value={billsStore} label="Store" onChange={e => setBillsStore(e.target.value as number | 'all')}>
                {storeOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <DatePicker label="From" value={billsFrom} onChange={setBillsFrom}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
            <DatePicker label="To" value={billsTo} onChange={setBillsTo}
              slotProps={{ textField: { size: 'small', sx: { width: 150 } } }} />
            <TextField size="small" label="Entry By" value={entryByFilter}
              onChange={e => setEntryByFilter(e.target.value)} sx={{ width: 140 }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Payment</InputLabel>
              <Select value={pmFilter} label="Payment" onChange={e => setPmFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="upi">UPI</MenuItem>
                <MenuItem value="cc">Card</MenuItem>
                <MenuItem value="__unpaid__">Unpaid</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" size="small" onClick={loadBills} disabled={billsLoading}>Search</Button>
            <Button size="small" startIcon={<Download />} onClick={exportBillsCSV} disabled={bills.length === 0}>CSV</Button>
          </Box>

          {billsLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell><strong>Bill No</strong></TableCell>
                    <TableCell><strong>Bill Date</strong></TableCell>
                    <TableCell><strong>Entry By</strong></TableCell>
                    <TableCell><strong>Patient</strong></TableCell>
                    <TableCell><strong>Mobile</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell align="center"><strong>Payment</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bills.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No bills found
                      </TableCell>
                    </TableRow>
                  ) : bills.map(b => (
                    <TableRow key={b.id} hover>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{b.bill_no}</TableCell>
                      <TableCell>{fmtDate(b.bill_date)}</TableCell>
                      <TableCell>{b.entry_by || '—'}</TableCell>
                      <TableCell>{b.patient_name || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{b.mobile || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(b.bill_amount)}</TableCell>
                      <TableCell align="center">{paymentChip(b.payment_method)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {bills.length === 1000 && (
                <Box sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    Showing first 1000 results — use filters to narrow down
                  </Typography>
                </Box>
              )}
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EvitalrxSales;
